import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, getFirestore } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from 'firebase/storage';
import InputMask from 'react-input-mask';
import Button from '../common/Button'; // Importando o botão genérico

const DadosProfissionais = () => {
  const auth = getAuth();
  const db = getFirestore();
  const storage = getStorage();
  const user = auth.currentUser;

  const [crm, setCrm] = useState('');
  const [estadoCrm, setEstadoCrm] = useState('');
  const [especialidade, setEspecialidade] = useState('');
  const [telefone, setTelefone] = useState('');
  const [enderecoComercial, setEnderecoComercial] = useState('');
  const [cidadeComercial, setCidadeComercial] = useState('');
  const [estadoComercial, setEstadoComercial] = useState('');
  const [cepComercial, setCepComercial] = useState('');
  const [logoURL, setLogoURL] = useState(''); // Estado para a URL da logo
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchProfessionalData = async () => {
      if (user) {
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const data = userDoc.data();
          setCrm(data.crm || '');
          setEstadoCrm(data.estadoCrm || '');
          setEspecialidade(data.especialidade || '');
          setTelefone(data.telefone || '');
          setEnderecoComercial(data.enderecoComercial || '');
          setCidadeComercial(data.cidadeComercial || '');
          setEstadoComercial(data.estadoComercial || '');
          setCepComercial(data.cepComercial || '');
          setLogoURL(data.logoURL || ''); // Carregar a URL da logo, se existir
        }
      }
    };

    fetchProfessionalData();
  }, [user, db]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!['image/jpeg', 'image/png'].includes(file.type)) {
        setMessage(
          'Formato de arquivo inválido. Por favor, selecione um arquivo JPG ou PNG.'
        );
        return;
      }

      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        if (img.width > 200 || img.height > 200) {
          setMessage(
            'O tamanho da imagem deve ser de no máximo 200x200 pixels.'
          );
        } else {
          setSelectedFile(file);
          setMessage('');
        }
      };
    }
  };

  const handleSaveProfessionalData = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      let logoDownloadURL = logoURL;

      if (selectedFile) {
        const fileName = `${user.uid}_logo_${selectedFile.name}`;
        const storageRef = ref(storage, `logos/${user.uid}/${fileName}`);
        const uploadTask = uploadBytesResumable(storageRef, selectedFile);

        logoDownloadURL = await new Promise((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            null,
            (error) => reject(error),
            () => getDownloadURL(uploadTask.snapshot.ref).then(resolve)
          );
        });

        setLogoURL(logoDownloadURL);
      }

      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        crm,
        estadoCrm,
        especialidade,
        telefone,
        enderecoComercial,
        cidadeComercial,
        estadoComercial,
        cepComercial,
        logoURL: logoDownloadURL, // Salvar a URL da logo no Firestore
      });

      setMessage('Dados profissionais atualizados com sucesso!');
    } catch (error) {
      setMessage('Erro ao atualizar dados profissionais: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSaveProfessionalData} className="flex flex-col gap-4">
      <div className="input-group">
        <label htmlFor="crm" className="text-gray-700">
          CRM:
        </label>
        <input
          type="text"
          id="crm"
          name="crm"
          placeholder="Digite seu CRM"
          value={crm}
          onChange={(e) => setCrm(e.target.value)}
          required
          disabled={loading}
          className="w-full p-2 border border-gray-300 rounded-lg focus:border-teal-600 focus:ring-teal-600"
        />
      </div>

      <div className="input-group">
        <label htmlFor="estadoCrm" className="text-gray-700">
          Estado do CRM:
        </label>
        <input
          type="text"
          id="estadoCrm"
          name="estadoCrm"
          placeholder="Digite o estado do CRM"
          value={estadoCrm}
          onChange={(e) => setEstadoCrm(e.target.value)}
          required
          disabled={loading}
          className="w-full p-2 border border-gray-300 rounded-lg focus:border-teal-600 focus:ring-teal-600"
        />
      </div>

      <div className="input-group">
        <label htmlFor="especialidade" className="text-gray-700">
          Especialidade:
        </label>
        <input
          type="text"
          id="especialidade"
          name="especialidade"
          placeholder="Digite sua especialidade"
          value={especialidade}
          onChange={(e) => setEspecialidade(e.target.value)}
          required
          disabled={loading}
          className="w-full p-2 border border-gray-300 rounded-lg focus:border-teal-600 focus:ring-teal-600"
        />
      </div>

      <div className="input-group">
        <label htmlFor="telefone" className="text-gray-700">
          Telefone:
        </label>
        <InputMask
          mask="(99) 99999-9999"
          type="text"
          id="telefone"
          name="telefone"
          placeholder="Digite seu telefone"
          value={telefone}
          onChange={(e) => setTelefone(e.target.value)}
          required
          disabled={loading}
          className="w-full p-2 border border-gray-300 rounded-lg focus:border-teal-600 focus:ring-teal-600"
        />
      </div>

      <h5 className="text-lg font-semibold">
        Endereço Comercial de Atendimento
      </h5>
      <hr className="border-gray-300" />

      <div className="input-group">
        <label htmlFor="enderecoComercial" className="text-gray-700">
          Endereço:
        </label>
        <input
          type="text"
          id="enderecoComercial"
          name="enderecoComercial"
          placeholder="Digite o endereço comercial"
          value={enderecoComercial}
          onChange={(e) => setEnderecoComercial(e.target.value)}
          required
          disabled={loading}
          className="w-full p-2 border border-gray-300 rounded-lg focus:border-teal-600 focus:ring-teal-600"
        />
      </div>

      <div className="input-group">
        <label htmlFor="cidadeComercial" className="text-gray-700">
          Cidade:
        </label>
        <input
          type="text"
          id="cidadeComercial"
          name="cidadeComercial"
          placeholder="Digite a cidade"
          value={cidadeComercial}
          onChange={(e) => setCidadeComercial(e.target.value)}
          required
          disabled={loading}
          className="w-full p-2 border border-gray-300 rounded-lg focus:border-teal-600 focus:ring-teal-600"
        />
      </div>

      <div className="input-group">
        <label htmlFor="estadoComercial" className="text-gray-700">
          Estado:
        </label>
        <input
          type="text"
          id="estadoComercial"
          name="estadoComercial"
          placeholder="Digite o estado"
          value={estadoComercial}
          onChange={(e) => setEstadoComercial(e.target.value)}
          required
          disabled={loading}
          className="w-full p-2 border border-gray-300 rounded-lg focus:border-teal-600 focus:ring-teal-600"
        />
      </div>

      <div className="input-group">
        <label htmlFor="cepComercial" className="text-gray-700">
          CEP:
        </label>
        <InputMask
          mask="99999-999"
          type="text"
          id="cepComercial"
          name="cepComercial"
          placeholder="Digite o CEP"
          value={cepComercial}
          onChange={(e) => setCepComercial(e.target.value)}
          required
          disabled={loading}
          className="w-full p-2 border border-gray-300 rounded-lg focus:border-teal-600 focus:ring-teal-600"
        />
      </div>

      {/* Campo para upload da logo */}
      <div className="input-group">
        <label htmlFor="logo" className="text-gray-700">
          Logo (máx 200x200px):
        </label>
        <input
          type="file"
          id="logo"
          name="logo"
          accept=".jpg,.jpeg,.png"
          onChange={handleFileChange}
          disabled={loading}
          className="w-full p-2 border border-gray-300 rounded-lg focus:border-teal-600 focus:ring-teal-600"
        />
        {logoURL && (
          <img
            src={logoURL}
            alt="Logo"
            className="mt-4 w-[200px] h-[100px] rounded border border-gray-300 object-none"
          />
        )}
      </div>

      {/* Substituindo o botão de "Salvar Dados" pelo botão genérico */}
      <Button
        type="salvar"
        label={loading ? 'Salvando...' : 'Salvar Dados'}
        onClick={handleSaveProfessionalData}
        className=""
        disabled={loading}
      />

      {message && (
        <div className="text-teal-600 font-semibold mt-4">{message}</div>
      )}
    </form>
  );
};

export default DadosProfissionais;
